import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  withHandlers,
  lifecycle
} from 'recompose';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { addGroupAccount } from '../../actions/Accounts';
import SendCodeButton from '../../components/atoms/SendCodeButton';
import BackButton from '../../components/atoms/BackButton';
import RegisterButton from '../../components/atoms/RegisterButton';
import RequiredButton from '../../components/atoms/RequiredButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import ImagePreview from '../../components/molecules/ImagePreview';
import { addGroupImage } from '../../actions/Groups';
import {
  signIn,
  getCurrentUser,
  setPreferredMFA,
  updatePhone,
  verifyPhone,
  verifyPhoneSubmit,
  sendLogin,
  resizeImage,
  uploadFile
} from '../../auth/Auth';

const styles = {
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none'
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px'
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  description: {
    color: '#707070',
    margin: '20px 0px 0px'
  },
  subTitle: {
    margin: '20px 0px 0px',
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  divider: {
    margin: '20px 0px'
  },
  detailTable: {
    width: 'auto'
  },
  tableCellItem: {
    border: '2px solid #ffffff',
    backgroundColor: '#e0e0e0'
  },
  tableCellData: {
    border: '2px solid #ffffff'
  },
  tableCell90: {
    width: '90%'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px'
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px'
  },
  editTable: {
    width: 'auto'
  },
  tableCell: {
    border: '2px solid #ffffff'
  },
  caution: {
    color: 'red',
    fontSize: '1.1rem'
  },
  breakLines: {
    whiteSpace: 'break-spaces'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name,
      prefectures: state.commons.prefectures
    }),
    dispatch => ({
      actions: bindActionCreators({ addGroupAccount, addGroupImage }, dispatch)
    })
  ),
  setPropTypes(propTypes),
  withState('openGroupAddProgress', 'updateOpenGroupAddProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isGroupAddSuccess', 'updateIsGroupAddSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('prefecture', 'setPrefecture', 0),
  withState('phone', 'setPhone', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {}
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    openGroupAddProgress,
    updateOpenGroupAddProgress,
    loading,
    updateLoading,
    isGroupAddSuccess,
    updateIsGroupAddSuccess,
    resultMessage,
    setResultMessage,
    handleRedirect,
    backAction,
    addConfirm,
    addParam,
    email,
    password,
    phone,
    setPhone,
    phoneAuthCode,
    setPhoneAuthCode,
    prefectures,
    updateOpenGroupAddConfirm,
    setCognitoUserId
  }) => {
    const sendCodeAction = async () => {
      updateLoading(true);
      updateOpenGroupAddProgress(true);
      signIn(email, password)
        .then(signInUser => {
          setCognitoUserId(signInUser.getUsername());
          getCurrentUser()
            .then(async user => {
              const phoneWithCountry = `+81${phone.replace(/-/g, '').slice(1)}`;
              await setPreferredMFA(user, 'NOMFA');
              await updatePhone(user, phoneWithCountry);
              await verifyPhone(user);
              setResultMessage('認証コードを送信しました。');
              updateLoading(false);
            })
            .catch(() => {
              setResultMessage('システムエラーが発生しました。');
              updateLoading(false);
            });
        })
        .catch(() => {
          setResultMessage('システムエラーが発生しました。');
          updateLoading(false);
        });
    };
    const registerAction = async () => {
      updateLoading(true);
      updateOpenGroupAddProgress(true);
      getCurrentUser()
        .then(async user => {
          await verifyPhoneSubmit(user, phoneAuthCode);
          // await setPreferredMFA(user, 'SMS');
          const addedGroup = await actions
            .addGroupAccount(addParam)
            .then(data => checkSession(data));

          // upload image
          const imagePaths = await Promise.all(
            Object.keys(addParam.images).map(async i => {
              if (addParam.images[i].image !== null) {
                let { image } = addParam.images[i];
                if (image.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
                  const { resizeImageFile } = await resizeImage(image);
                  image = resizeImageFile;
                }
                const imagePath = await uploadFile(
                  'group',
                  addedGroup.payload.group.groupId,
                  image,
                  'public'
                );
                return imagePath;
              }
              return null;
            })
          );
          const images = imagePaths
            .filter(i => i !== null)
            .map((i, index) => {
              return { image: i, viewOrder: index };
            });
          if (Object.keys(images).length !== 0) {
            await actions
              .addGroupImage(addedGroup.payload.group.groupId, {
                images
              })
              .then(data => checkSession(data));
          }

          if (addedGroup.type === 'ADD_GROUP_ACCOUNT_SUCCESS') {
            updateIsGroupAddSuccess(true);
            setResultMessage(
              '登録が完了しました。登録したメールアドレス/パスワードでログインしてください。'
            );
            updateLoading(false);
          } else {
            updateIsGroupAddSuccess(false);
            setResultMessage('登録に失敗しました。');
            updateLoading(false);
          }
        })
        .catch(() => {
          updateIsGroupAddSuccess(false);
          setResultMessage('システムエラーが発生しました。');
          updateLoading(false);
        });
    };

    const progressOkAction = () => {
      if (isGroupAddSuccess) {
        updateOpenGroupAddConfirm(false);
        updateOpenGroupAddProgress(false);
        handleRedirect('/');
      } else {
        updateOpenGroupAddProgress(false);
      }
    };

    const groupDisplayLimit =
      addConfirm.groupType === 'CAMPAIGN' ||
      addConfirm.groupType === 'OTHERS' ||
      addConfirm.groupType === 'GOCHI_KID';

    return (
      <div className={classes.root}>
        <Typography variant="h6">グループ運営会社登録申込書</Typography>
        <Paper className={classes.mainPaper} elevation={1}>
          <Table className={classes.detailTable}>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                貴グループ運営会社名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.groupName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>郵便番号</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.zipCode}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>都道府県</TableCell>
              <TableCell className={classes.tableCellData}>
                {prefectures.map(item => {
                  if (
                    addConfirm.prefectureCode.toString() === item.id.toString()
                  ) {
                    return item.name;
                  }
                  return '';
                })}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>市区町村</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.city}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                丁目・番地
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.address}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>建物名</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.building}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>電話番号</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.phone}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>FAX</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.fax}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                ご担当者名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.groupContactName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>所属部署</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.department}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                電話番号（直通等）
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.directPhone}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>画像</TableCell>
              <TableCell className={classes.tableCellData}>
                <ImagePreview previewImage={addConfirm.previewImage1} />
                <ImagePreview previewImage={addConfirm.previewImage2} />
                <ImagePreview previewImage={addConfirm.previewImage3} />
                <ImagePreview previewImage={addConfirm.previewImage4} />
                <ImagePreview previewImage={addConfirm.previewImage5} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループの概要
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.description}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                WebページURL
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.website}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループ種別
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.groupTypeStr}
              </TableCell>
            </TableRow>
            {!groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  入金管理
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.depositManageStr}
                </TableCell>
              </TableRow>
            )}
            {addParam.groupType !== 'OTHERS' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  社食ごちめし現金授受の有無
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.cashAllowed ? '受け付ける' : '受け付けない'}
                </TableCell>
              </TableRow>
            )}
            {addParam.groupType !== 'OTHERS' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  社食ごちめし金額指定チケット利用の有無
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.allowMenuPriceShashoku
                    ? '利用する'
                    : '利用しない'}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>銀行名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankName}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>支店名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankBranchName}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座種別
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountTypeStr}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座番号
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountNumber}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座名義
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountHolder}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループ紐付け時のメッセージ
              </TableCell>
              <TableCell
                className={`${classes.tableCellData} ${classes.breakLines}`}
              >
                {addConfirm.linkingShopMessage}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                メールアドレス
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.email}
              </TableCell>
            </TableRow>
          </Table>
          <Divider className={classes.divider} />
          <div className={classes.subTitle}>
            <Typography variant="h6">２段階認証</Typography>
            <Typography className={classes.description}>
              SMS認証を行いますので、認証コードが送付可能な携帯電話番号をご入力ください。
              <br />
              携帯電話番号入力後、認証コードを送信し、SMSで届いた認証コードをご入力ください。
            </Typography>
          </div>
          <Table className={classes.editTable}>
            <TableRow>
              <TableCell className={classes.tableCell}>
                <RequiredButton />
              </TableCell>
              <TableCell
                className={classNames(classes.tableCell, classes.tableCell90)}
              >
                <TextField
                  required
                  label="電話番号"
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setPhone(e.target.value);
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCell} />
              <TableCell className={classes.tableCell}>
                <SendCodeButton onClickAction={sendCodeAction} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCell} />
              <TableCell
                className={classNames(classes.tableCell, classes.caution)}
              >
                SMS認証コードが届くまでに時間がかかる場合があります。
                SMSの認証コード送信ボタンを２回押すと必ずエラーとなり進めなくなりますので、そのままお待ちください。
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCell}>
                <RequiredButton />
              </TableCell>
              <TableCell className={classes.tableCell}>
                <TextField
                  required
                  label="認証コード"
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setPhoneAuthCode(e.target.value);
                  }}
                />
              </TableCell>
            </TableRow>
          </Table>
        </Paper>
        <div>
          <BackButton onClickAction={backAction} />
          <RegisterButton onClickAction={registerAction} />
        </div>
        <ProgressDialog
          openProgress={openGroupAddProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
